import { AlertTriangleIcon } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useDeleteAllParticipantsReminders } from "@/hooks/useDeleteAllParticipantsReminders";
import { useDeleteEvent } from "@/hooks/useDeleteEvent";
import type { EventApiResponse, RecurrenceScope } from "@/lib/humhub-api/types";

interface EventDeleteContentProps {
  eventData: EventApiResponse;
  onDeleteSuccess: () => void;
  onCancel: () => void;
  initialScope?: RecurrenceScope;
}

export const EventDeleteContent: React.FC<EventDeleteContentProps> = ({
  eventData,
  onDeleteSuccess,
  onCancel,
  initialScope,
}) => {
  const { t } = useTranslation();
  const {
    isLoading: deleteLoading,
    error: deleteError,
    deleteEvent,
  } = useDeleteEvent();
  const {
    isLoading: deleteRemindersLoading,
    error: deleteRemindersError,
    deleteAllParticipantsReminders,
  } = useDeleteAllParticipantsReminders();
  const [deleteScope] = useState<RecurrenceScope>(initialScope ?? "single");

  const handleDelete = async () => {
    try {
      // まずリマインダーを削除
      try {
        await deleteAllParticipantsReminders(eventData.id, deleteScope);
      } catch (reminderError) {
        console.error("Failed to delete reminders:", reminderError);
        // リマインダー削除の失敗はイベント削除には影響しないので続行
      }

      // イベントを削除
      await deleteEvent(eventData.id, deleteScope);
      onDeleteSuccess();
    } catch (error) {
      console.error("Failed to delete event:", error);
    }
  };

  const isLoading = deleteLoading || deleteRemindersLoading;
  const error = deleteError || deleteRemindersError;

  return (
    <div className="flex h-full flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center space-x-1">
        <AlertTriangleIcon className="size-5 text-rw-red" aria-hidden="true" />
        <h2 className="text-lg font-medium text-rw-black">
          {t("eventManage.deleteEvent")}
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 text-rw-black-gray">
        <p className="text-sm">{t("eventManage.deleteConfirmation")}</p>
      </div>

      {/* Footer */}
      <div className="flex w-full justify-end space-x-3">
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="destructive"
          onClick={handleDelete}
          disabled={isLoading}
        >
          {t("eventManage.deleteEvent")}
        </Button>
      </div>
      {error && (
        <p className="mt-2 w-full text-center text-sm text-rw-red">
          {t("common.error")}: {error.message}
        </p>
      )}
    </div>
  );
};
